import {
  CollectionReference,
  DocumentReference,
  Query,
  Unsubscribe,
  onSnapshot
} from '@firebase/firestore'
import { PiniaPluginContext } from 'pinia'

/**
 * NOTE: npmで公開してたけど、うまく動かなくなったので、ローカルに移植してしまった🙏
 * @param param0
 */
export const PiniaFirestoreSync = ({ store }: PiniaPluginContext) => {
  store.sync = (
    key: string,
    ref: DocumentReference | CollectionReference | Query
  ) => {
    // Document
    if (ref.type === 'document') {
      return onSnapshot(
        ref,
        (ds) => {
          if (ds.exists()) {
            const data = ds.data()
            Object.defineProperty(data, 'id', {
              value: ds.id,
              writable: false,
              enumerable: false
            })
            store.$patch((state) => {
              state[key] = data
            })
          }
        },
        (error) => {
          console.error(`onSnapshot failed for document ${ref.path}:`, error)
        }
      )
    }

    // Collection or Query
    return onSnapshot(
      ref,
      async (qs) => {
        const datum = qs.docs.map((d) => {
          const data = d.data()
          Object.defineProperty(data, 'id', {
            value: d.id,
            writable: false,
            enumerable: false
          })
          return data
        })
        store.$patch((state) => {
          state[key] = datum
        })
      },
      (error) => {
        const refPath = 'path' in ref ? ref.path : 'query'
        console.error(`onSnapshot failed for collection ${refPath}:`, error)
      }
    )
  }
}

declare module 'pinia' {
  // FIXME: Want to limit `key: string` to only keys of State
  export interface PiniaCustomProperties<Id, S, G, A> {
    /**
     *
     * @param key Key name of state which synchronize with firestore document data.
     * @param ref Document reference to sync
     *
     * @example
     *
     * ```ts
     * type ExampleDoc = {
     *   name: string,
     *   age: number
     * }
     *
     * export type State = {
     *   docData: ExampleDoc | null,
     * }
     *
     * export const useExampleStore = defineStore('expamle', {
     *   state: (): State => {
     *     return {
     *       docData: null,
     *     }
     *   },
     *   actions: {
     *     async setup() {
     *       // Get Document reference
     *       const store = getFirestore()
     *       const docRef = doc(store, 'Examples/id')
     *
     *       // Do the magic
     *       this.sync('docData', docRef)
     *     }
     *   }
     * })
     *```
     */
    sync(key: string, ref: DocumentReference | CollectionReference): Unsubscribe
    /**
     *
     * @param key Key name of state which synchronize with firestore collection data.
     * @param ref Collection reference to sync
     *
     * @example
     *
     * ```ts
     * type ExampleDoc = {
     *   name: string,
     *   age: number
     * }
     *
     * export type State = {
     *   collectionData: ExampleDoc[] | null,
     * }
     * export const useExampleStore = defineStore('expamle', {
     *   state: (): State => {
     *     return {
     *       collectionData: null,
     *     }
     *   },
     *   actions: {
     *     async setup() {
     *       // Get Collection reference
     *       const store = getFirestore()
     *       const collectionRef = collection(store, 'Examples')
     *
     *       // Do the magic
     *       this.sync('collectionData', collectionRef)
     *     }
     *   }
     * })
     *```
     */
    sync(key: string, ref: DocumentReference | CollectionReference): Unsubscribe
    /**
     *
     * @param key Key name of state which synchronize with firestore collection data.
     * @param ref Query to sync
     *
     * @example
     * ```ts
     * type ExampleDoc = {
     *   name: string,
     *   age: number
     * }
     * export type State = {
     *   queryData: ExampleDoc[] | null,
     * }
     * export const useExampleStore = defineStore('expamle', {
     *   state: (): State => {
     *     return {
     *       queryData: null,
     *     }
     *   },
     *   actions: {
     *     async setup() {
     *       // Build query
     *       const store = getFirestore()
     *       const collectionRef = collection(store, 'Examples')
     *       const q = query(collectionRef, where('name', '==', 'wombat'))
     *
     *       // Do the magic
     *       this.sync('queryData', q)
     *     }
     *   }
     * })
     * ```
     */
    sync(key: string, ref: Query): Unsubscribe
  }
}
